// src/pages/documents/TandaMasukListPage/TandaMasukListPage.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import OrdersAPI from '../../../api/v3/orders-api-v3';

const formatIDR = (num) => `Rp ${Number(num || 0).toLocaleString('id-ID')}`;

// HELPER FUNCTION
function normalizeOrderStatus(status) {
  return (status || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
}

function orderStatusBadgeMeta(status) {
  const normalized = normalizeOrderStatus(status);

  const map = {
    draft: { label: 'Pending', className: 'bg-warning text-dark' },
    confirmed: { label: 'Pending', className: 'bg-warning text-dark' },
    accepted: { label: 'Accepted', className: 'bg-info text-dark' },
    processing: { label: 'Processing', className: 'bg-primary' },
    completed: { label: 'Completed', className: 'bg-success' },
    complete: { label: 'Completed', className: 'bg-success' },
    cancelled: { label: 'Cancelled', className: 'bg-dark' },
    returned: { label: 'Returned', className: 'bg-secondary' },
    refunded: { label: 'Refunded', className: 'bg-secondary' },
    paid_dp: { label: 'Paid DP', className: 'bg-info text-dark' },
    paid_full: { label: 'Paid Full', className: 'bg-success' },
    vendor_processing: { label: 'Send to Vendor', className: 'bg-primary' },
    customer_pick_up_process: { label: 'Back to BNK / QC', className: 'bg-dark' },
    need_packing_customer: { label: 'Need Packing Customer', className: 'bg-info text-dark' },
  };

  return map[normalized] || {
    label: normalized ? normalized.replace(/_/g, ' ') : '-',
    className: 'bg-light text-dark'
  };
}

export default function TandaMasukListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [meta, setMeta] = useState({ page: 1, per: 25, total: 0, total_pages: 1 });

  // filters
  const [query, setQuery] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [hasVoucherOnly, setHasVoucherOnly] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(25); // 10/25/50/100

  // selection & async states
  const [selectedRows, setSelectedRows] = useState([]);
  const [downloading, setDownloading] = useState(null); // number | 'merge' | null

  const selectedIds = useMemo(() => selectedRows.map((row) => row.id), [selectedRows]);

  // ---------------- Data loader ----------------
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErr('');

      const params = { page, per };
      if (query) params.q = query;
      if (voucherCode) params.voucher_code = voucherCode;
      if (hasVoucherOnly) params.has_voucher_only = true;

      const json = await OrdersAPI.list(params);
      const data = json?.items || json?.data || json || [];
      const nextRows = Array.isArray(data) ? data : [];
      const nextMeta = json?.meta || {};

      setRows(nextRows);
      setMeta({
        page: Number(nextMeta.page || page),
        per: Number(nextMeta.per || per),
        total: Number(nextMeta.total || nextRows.length),
        total_pages: Number(nextMeta.total_pages || 1),
      });

      setSelectedRows((prev) =>
        prev.map((row) => nextRows.find((nextRow) => nextRow.id === row.id) || row)
      );
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [query, voucherCode, hasVoucherOnly, page, per]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Number(meta.total_pages || 1));
  const pageSafe = Math.min(page, totalPages);
  const startIdx = (pageSafe - 1) * per;
  const paged = rows;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ---------------- Merge baseline & guards ----------------
  const baseline = useMemo(() => {
    const base = selectedRows[0];
    if (!base) return null;

    return {
      name: (base.customer?.name || '').trim().toLowerCase(),
      created_at: base.created_at || null,
    };
  }, [selectedRows]);

  const isSameDay = (a, b) => {
    if (!a || !b) return false;
    return dayjs(a).isSame(dayjs(b), 'day');
  };

  const isRowSelectable = useCallback((row) => {
    if (!baseline) return true; // nothing selected yet -> anything can be the first
    const rowName = (row.customer?.name || '').trim().toLowerCase();
    const rowDay  = row.created_at;
    return rowName === baseline.name && isSameDay(rowDay, baseline.created_at);
  }, [baseline]);

  // ---------------- Helpers ----------------
  const toggleSelect = (row) => {
    if (!row) return;

    setSelectedRows((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (exists) return prev.filter((item) => item.id !== row.id);

      const base = prev[0];
      if (base) {
        const baseName = (base.customer?.name || '').trim().toLowerCase();
        const rowName = (row.customer?.name || '').trim().toLowerCase();

        if (rowName !== baseName || !isSameDay(row.created_at, base.created_at)) {
          alert(`Hanya bisa pilih yang NAMEnya sama & TANGGALnya sama (same day) (${baseName} • ${dayjs(base.created_at).format('YYYY-MM-DD')}).`);
          return prev;
        }
      }

      return [...prev, row];
    });
  };

  const toggleSelectAllVisible = (checked) => {
    if (!checked) {
      const visibleIds = new Set(paged.map((r) => r.id));
      setSelectedRows((prev) => prev.filter((row) => !visibleIds.has(row.id)));
      return;
    }

    let base = baseline;
    if (!base && paged.length) {
      const firstRow = paged[0];
      base = {
        name: (firstRow.customer?.name || '').trim().toLowerCase(),
        created_at: firstRow.created_at || null,
      };
    }

    const add = paged.filter((r) => {
      if (!base) return false;
      const rowName = (r.customer?.name || '').trim().toLowerCase();
      return rowName === base.name && isSameDay(r.created_at, base.created_at);
    });

    setSelectedRows((prev) => {
      const map = new Map(prev.map((row) => [row.id, row]));
      add.forEach((row) => map.set(row.id, row));
      return Array.from(map.values());
    });
  };

  const clearSelection = () => setSelectedRows([]);

  // local quick preview (not authoritative — backend still validates)
  const selectionPreview = useMemo(() => {
    const uniqueCustomers = Array.from(new Set(selectedRows.map((r) => r.customer?.name || '-')));
    const uniqueDays = Array.from(new Set(selectedRows.map((r) => dayjs(r.created_at).format('YYYY-MM-DD'))));
    return { uniqueCustomers, uniqueDays, count: selectedRows.length };
  }, [selectedRows]);

  // ---------------- Actions ----------------
  async function onPrint(id) {
    setDownloading(id);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE;
      const url = `${API_BASE}/api/v3/orders/${id}/customer_receipt`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to generate Customer Receipt for ID ${id}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `Customer_Receipt_${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  }

  async function onCancel(id) {
    if (!window.confirm('Batalkan order ini?')) return;
    try {
      await OrdersAPI.update(id, { status: 'cancelled' });
      setSelectedRows((prev) => prev.filter((row) => row.id !== id));
      await load();
    } catch (e) {
      alert(e.message);
    }
  }

  async function onConfirmOrders() {
    if (!selectedIds.length) return alert('Pilih minimal 1 order untuk dikonfirmasi.');
    if (!window.confirm('Konfirmasi semua order terpilih?')) return;
    try {
      for (const id of selectedIds) {
        await OrdersAPI.update(id, { status: 'accepted' });
      }
      alert('✅ Order telah dikonfirmasi.');
      setSelectedRows([]);
      await load();
    } catch (e) {
      alert(e.message);
    }
  }

  async function onMergeSelected() {
    if (!selectedIds.length) return alert('Pilih minimal 1 order untuk digabung.');
    // Frontend already constrains selection; still, keep help text:
    if (selectionPreview.uniqueCustomers.length > 1 || selectionPreview.uniqueDays.length > 1) {
      alert('Merge hanya untuk NAMA dan TANGGAL (same day) yang sama.');
      return;
    }
    setDownloading('merge');
    try {
      await OrdersAPI.downloadMergedCustomerReceipts(selectedIds);
    } catch (e) {
      alert(e.message);
    } finally {
      setDownloading(null);
    }
  }

  const selectableVisible = paged.filter((r) => isRowSelectable(r));

  return (
    <div className="bagspa-page tanda-masuk-page">
      <div className="bagspa-page-header tanda-masuk-page-header">
        <h3 className="mb-0">Tanda Masuk + Customer Receipt</h3>
      </div>

      <div className="bagspa-filter-card tanda-masuk-control-card">
        {/* TOP ROW */}
        <div className="tanda-masuk-control-top-row">
          <div className="bagspa-filter-field tanda-masuk-filter-search">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Cari nama customer / order no / voucher code…"
              value={query}
              onChange={e => {
                setPage(1);
                setSelectedRows([]);
                setQuery(e.target.value);
              }}
            />
          </div>

          <div className="bagspa-filter-field tanda-masuk-filter-voucher">
            <label className="form-label">Voucher</label>
            <input
              type="text"
              className="form-control"
              placeholder="Filter voucher code"
              value={voucherCode}
              onChange={e => {
                setPage(1);
                setSelectedRows([]);
                setVoucherCode(e.target.value);
              }}
            />
          </div>

          <div className="bagspa-filter-field tanda-masuk-filter-has-voucher">
            <label className="form-label">Filter</label>
            <div className="bagspa-check-row tanda-masuk-check-row">
              <div className="form-check">
                <input
                  className="form-check-input"
                  id="tm-has-voucher"
                  type="checkbox"
                  checked={hasVoucherOnly}
                  onChange={e => {
                    setPage(1);
                    setSelectedRows([]);
                    setHasVoucherOnly(e.target.checked);
                  }}
                />
                <label className="form-check-label" htmlFor="tm-has-voucher">
                  Has voucher only
                </label>
              </div>
            </div>
          </div>

          <div className="tanda-masuk-filter-apply">
            <button className="btn btn-primary" onClick={load}>
              Apply
            </button>
          </div>
        </div>

        {/* BOTTOM ROW */}
        <div className="tanda-masuk-control-bottom-row">
          <div className="tanda-masuk-bottom-left">
            <div className="bagspa-filter-field tanda-masuk-filter-rows">
              <label className="form-label">Rows</label>
              <select
                className="form-control"
                value={per}
                onChange={(e) => {
                  setPer(parseInt(e.target.value, 10) || 25);
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map(n => (
                  <option key={n} value={n}>{n}/page</option>
                ))}
              </select>
            </div>

            {!!selectedIds.length && (
              <button
                className="btn btn-outline-secondary tanda-masuk-clear-selection"
                onClick={clearSelection}
              >
                Clear Selection
              </button>
            )}
          </div>

          <div className="tanda-masuk-bottom-right">
            <button
              className="btn btn-primary"
              disabled={!selectedIds.length || downloading === 'merge'}
              onClick={onMergeSelected}
              title="Hanya bisa merge untuk NAMA & TANGGAL (same day) yang sama"
            >
              {downloading === 'merge' ? 'Merging…' : '🧾 Merge Selected Receipts'}
            </button>

            <button
              className="btn btn-success"
              disabled={!selectedIds.length}
              onClick={onConfirmOrders}
            >
              ✅ Confirm Selected Orders
            </button>
          </div>
        </div>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      {!!selectedIds.length && (
        <div className="alert alert-info tanda-masuk-selection-summary" role="status">
          <div className="tanda-masuk-selection-summary__title">
            Selected Receipt Summary
          </div>

          <div className="tanda-masuk-selection-summary__content">
            <span className="tanda-masuk-selection-pill">
              Selected: <strong>{selectedIds.length}</strong>
            </span>

            <span className="tanda-masuk-selection-pill">
              Customers: <strong>{selectionPreview.uniqueCustomers.length}</strong>
              {selectionPreview.uniqueCustomers.length <= 3
                ? ` (${selectionPreview.uniqueCustomers.join(' • ')})`
                : ''}
            </span>

            <span className="tanda-masuk-selection-pill">
              Days: <strong>{selectionPreview.uniqueDays.length}</strong>
              {selectionPreview.uniqueDays.length <= 3
                ? ` (${selectionPreview.uniqueDays.join(', ')})`
                : ''}
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="bagspa-table-card tanda-masuk-table-card">
          <div className="table-responsive">
            <table className="table table-sm table-bordered align-middle tanda-masuk-table">
              <thead className="table-light">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectableVisible.length > 0 &&
                        selectableVisible.every((r) => selectedIds.includes(r.id))
                      }
                      onChange={e => toggleSelectAllVisible(e.target.checked)}
                    />
                  </th>
                  <th>No</th>
                  <th>Order No</th>
                  <th>Status</th>
                  <th>Voucher</th>
                  <th>Customer</th>
                  <th>Layanan</th>
                  <th>Advance</th>
                  <th>Final</th>
                  <th>Sisa</th>
                  <th>Tanggal</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paged.length ? paged.map((r, i) => {
                  const adv = Number(r.advance_payment_price || 0);
                  const fin = Number(r.final_fixed_price || 0);
                  const remain = fin - adv;
                  const disabled = baseline ? !isRowSelectable(r) : false;

                  return (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelect(r)}
                          disabled={disabled}
                          title={disabled ? 'Hanya bisa pilih yang NAMA & TANGGALnya sama (same day) dengan pilihan pertama' : undefined}
                        />
                      </td>

                      <td>{startIdx + i + 1}</td>
                      <td>{r.order_no}</td>

                      <td>
                        {(() => {
                          const badge = orderStatusBadgeMeta(r.status);
                          return <span className={`badge ${badge.className}`}>{badge.label}</span>;
                        })()}
                      </td>

                      <td>
                        {r.voucher ? (
                          <>
                            <span className="badge bg-info-subtle text-info-emphasis">
                              {r.voucher.code}
                            </span>
                            <div className="small text-muted">
                              - {formatIDR(r.voucher.discount_amount)}
                            </div>
                            {r.voucher.redeemed_at && (
                              <div className="small text-muted">
                                {dayjs(r.voucher.redeemed_at).format('DD MMM YYYY HH:mm')}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>

                      <td>{r.customer?.name || '-'}</td>
                      <td>{r.order_category_name || '—'}</td>
                      <td>{formatIDR(adv)}</td>
                      <td>{formatIDR(fin)}</td>
                      <td>{formatIDR(remain)}</td>
                      <td>{r.created_at ? dayjs(r.created_at).format('DD/MM/YYYY') : '—'}</td>

                      <td>
                        <div className="btn-group tanda-masuk-row-actions">
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => onPrint(r.id)}
                            disabled={downloading === r.id}
                          >
                            {downloading === r.id ? 'Downloading…' : 'Download PDF'}
                          </button>

                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => onCancel(r.id)}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan={12} className="text-center text-muted">
                      Tidak ada data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="bagspa-pagination-row tanda-masuk-pagination-row">
            <div className="text-muted small">
              Total: {meta.total} • Page {pageSafe} / {totalPages}
            </div>

            <div className="btn-group">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                &lsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                &rsaquo;
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={pageSafe >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
